import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { OrderAPI } from '@certi/shared/api/order/OrderAPI'
import type { UserOrderItemResponse } from '@certi/shared/types'

export default function CheckoutSuccessPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, session } = useAuth()

  const [order, setOrder] = useState<UserOrderItemResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [verified, setVerified] = useState(false)
  const [checkingTracking, setCheckingTracking] = useState(false)

  useEffect(() => {
    if (!user || !session?.accessToken || !orderId) {
      navigate('/login')
      return
    }

    let cancelled = false
    const checkoutSessionId = searchParams.get('session_id')

    const verifyPayment = async () => {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        let foundOrder: UserOrderItemResponse | null = null

        if (attempt === 0 && checkoutSessionId) {
          try {
            foundOrder = await OrderAPI.confirmCheckoutSession(orderId, checkoutSessionId, session.accessToken)
          } catch (confirmError) {
            console.warn('Checkout session confirmation fell back to order polling:', confirmError)
          }
        }

        if (!foundOrder) {
          const orders = await OrderAPI.getUserAllOrders(session.accessToken)
          foundOrder = orders.find(candidate => candidate.paymentId === orderId) || null
        }

        if (cancelled) {
          return
        }

        setOrder(foundOrder)

        if (foundOrder?.isPaid) {
          setVerified(true)
          setLoading(false)
          if (foundOrder.shippingTrackingNumber || isShippingLabelFailed(foundOrder)) {
            setCheckingTracking(false)
            return
          }
          setCheckingTracking(true)
        }

        if (foundOrder?.isPaid && attempt >= 11) {
          setCheckingTracking(false)
          return
        }

        await new Promise(resolve => window.setTimeout(resolve, 2000))
      }

      if (!cancelled) {
        setLoading(false)
        setCheckingTracking(false)
      }
    }

    verifyPayment().catch(error => {
      console.error('Failed to verify order payment:', error)
      if (!cancelled) {
        setLoading(false)
        setCheckingTracking(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [navigate, orderId, searchParams, session, user])

  const labelFailed = order ? isShippingLabelFailed(order) : false

  return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-green-100 p-6">
            <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {verified ? 'Payment Successful!' : 'Finalizing Your Payment'}
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          {verified
            ? order?.shippingTrackingNumber
              ? 'Your payment has been processed and tracking is ready.'
              : labelFailed
                ? 'Your payment has been processed, but the shipping label needs review.'
                : 'Your payment has been processed. We are preparing your shipping label now.'
            : 'Stripe accepted your payment. We are waiting for the final confirmation from our backend.'}
        </p>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-8 text-left">
          <h2 className="font-semibold text-gray-900 mb-4">
            {loading ? 'Confirming Payment Status...' : "What's Next?"}
          </h2>

          {!verified && (
            <p className="text-sm text-gray-600 mb-4">
              This usually completes within a few seconds. If the order still looks unpaid, refresh your orders page once.
            </p>
          )}

          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-brand-primary text-white w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                1
              </div>
              <div>
                <p className="font-medium">{verified ? 'Payment Confirmed' : 'Backend Confirmation In Progress'}</p>
                <p className="text-gray-600">
                  {verified
                    ? 'Your order is now confirmed and visible in My Orders'
                    : 'We are waiting for the payment webhook to update your order'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-brand-primary text-white w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                2
              </div>
              <div>
                <p className="font-medium">Certi Prepares Shipment</p>
                <p className="text-gray-600">
                  {checkingTracking
                    ? 'We are purchasing the shipping label and saving tracking details'
                    : 'Our team prepares your order for authenticated fulfillment'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-brand-primary text-white w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                3
              </div>
              <div>
                <p className="font-medium">Shipping Updates</p>
                <p className="text-gray-600">Track fulfillment status from your orders dashboard</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-brand-primary text-white w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                4
              </div>
              <div>
                <p className="font-medium">Delivery</p>
                <p className="text-gray-600">Receive your authenticated order</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/profile/orders')}
            className="btn btn-primary px-8 py-3"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            View My Orders
          </button>
          <button
            onClick={() => navigate('/marketplace')}
            className="btn btn-secondary px-8 py-3 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Continue Shopping
          </button>
        </div>

        {order?.shippingTrackingNumber ? (
          <div className="mt-8 rounded-lg border border-green-200 bg-green-50 p-4 text-left">
            <p className="text-sm font-semibold text-green-900">Tracking number</p>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-sm text-green-950">{order.shippingTrackingNumber}</span>
              {order.shippingTrackingUrl ? (
                <a
                  href={order.shippingTrackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-semibold text-brand-primary hover:underline"
                >
                  Track package
                </a>
              ) : null}
            </div>
          </div>
        ) : labelFailed ? (
          <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4 text-left">
            <p className="text-sm font-semibold text-red-900">Shipping label needs review</p>
            <p className="mt-1 text-sm text-red-800">
              The order is paid, but the carrier label was not created automatically.
            </p>
            {order?.shippingLabelError ? (
              <p className="mt-2 text-xs text-red-700">{order.shippingLabelError}</p>
            ) : null}
          </div>
        ) : verified ? (
          <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left">
            <p className="text-sm font-semibold text-amber-900">
              {checkingTracking ? 'Preparing tracking' : 'Tracking pending'}
            </p>
            <p className="mt-1 text-sm text-amber-800">
              Your order is paid. Tracking will appear here and in My Orders once the label is ready.
            </p>
          </div>
        ) : null}

        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Need help? Contact our{' '}
            <a href="mailto:support@certiauthentic.com" className="text-brand-primary hover:underline">
              support team
            </a>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Order ID: {order?.paymentId || orderId}
          </p>
        </div>
      </div>
    </div>
  )
}

function isShippingLabelFailed(order: UserOrderItemResponse) {
  const status = order.shippingLabelStatus?.toUpperCase()
  return status === 'ERROR' || status === 'FAILURE'
}
